import Chat, { Client } from 'twilio-chat';

import { ISourceTrackingOptions } from '../interfaces/IChatOptions';
import { retry } from './asyncUtils';

export interface ICreateChannelResult {
  sid: string;
  botEnabled: boolean;
  botName?: string;
  botGreeting?: string;
}

const createToken = async (env: string, identity: string = 'anonymous'): Promise<string> => {
  const resToken = await retry(
    () =>
      fetch(`${process.env.REACT_APP_CHAT_SERVICE_HOST}/token`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        method: 'POST',
        body: `env=${env}&identity=${encodeURIComponent(identity)}`,
      }),
    3,
    1000,
  ).catch((err) => {
    throw new Error(err);
  });

  if (resToken.status === 429) {
    throw new Error('blocked customer');
  }

  const dataToken = await resToken.json();

  if (!resToken.ok && dataToken.message) {
    throw new Error(dataToken.message);
  }

  return dataToken.token;
};

const createClient = async (token: string): Promise<Client> => {
  return Chat.create(token).catch((err) => {
    throw new Error(err);
  });
};

const createChannel = async (
  env: string,
  apiKey: string,
  identity: string = 'customer',
  name: string = 'Customer',
  affiliateKey?: string,
  sourceTracking?: ISourceTrackingOptions,
  targetWorker?: string,
): Promise<ICreateChannelResult> => {
  identity = encodeURIComponent(identity);
  apiKey = encodeURIComponent(apiKey);
  name = encodeURIComponent(name);
  let body = `env=${env}&identity=${identity}&apiKey=${apiKey}&name=${name}`;
  if (affiliateKey) {
    body += `&affiliateKey=${encodeURIComponent(affiliateKey)}`;
  }

  // pass on source tracking
  if (sourceTracking) {
    Object.keys(sourceTracking).forEach((key) => {
      body += `&${key}=${encodeURIComponent(sourceTracking[key])}`;
    });
  }

  if (targetWorker) {
    body += `&targetWorker=${targetWorker}`;
  }

  const resChannel = await retry(
    () =>
      fetch(`${process.env.REACT_APP_CHAT_SERVICE_HOST}/channel`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        method: 'POST',
        body,
      }),
    3,
    1000,
  ).catch((err) => {
    throw new Error(err);
  });

  if (resChannel.status === 429) {
    throw new Error('blocked customer');
  }

  const dataChannel = await resChannel.json();

  if (!resChannel.ok && dataChannel.message) {
    throw new Error(dataChannel.message);
  }

  return {
    sid: dataChannel.sid as string,
    botEnabled: dataChannel.botEnabled,
    botName: dataChannel.botName,
    botGreeting: dataChannel.botGreeting,
  };
};

// not deleting channel anymore

// const deleteChannel = async (env: string, channelSid: string): Promise<void> => {
//     await fetch(`${process.env.REACT_APP_CHAT_SERVICE_HOST}/channel/${channelSid}?env=${env}`, {
//         headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
//         method: 'DELETE',
//         body: `env=${env}`,
//     });
// };

const submitSurvey = async ({ env, channelSid, overallRating, options }) => {
  await fetch(`${process.env.REACT_APP_CHAT_SERVICE_HOST}/survey`, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    method: 'POST',
    body: `env=${env}&channel_id=${channelSid}&overall_rating=${overallRating}&options=${encodeURIComponent(options)}`,
  });
};

const chatService = {
  createToken,
  createClient,
  createChannel,
  submitSurvey,
};

export default chatService;
